import { useQuery } from "@tanstack/react-query";
import { createRng } from "@/lib/seededRandom";
import { CLIENTS, EMPLOYEES, REVENUE_BY_DEPARTMENT, REVENUE_HISTORY } from "@/data/mock";
import { withLatency } from "./simulate";

const rng = createRng(778899);

export const GROSS_PROFIT_PERCENT = 61.4;

function computeFinanceOverview() {
  const currentMonth = REVENUE_HISTORY[17];
  const mrr = currentMonth.mrr;
  const grossProfit = Math.round(mrr * (GROSS_PROFIT_PERCENT / 100));

  const activeEmployees = EMPLOYEES.filter((e) => e.status === "active");
  const avgMonthlyCost = 780;
  const payroll = activeEmployees.length * avgMonthlyCost;

  const collectionRate = 0.91;
  const collections = Math.round(mrr * collectionRate);
  const outstandingInvoices = Math.round(mrr * (1 - collectionRate) * 1.4);

  const vendorCostPool = Math.round((mrr - grossProfit) * 0.4);
  const apAging = {
    current: Math.round(vendorCostPool * 0.52),
    days30: Math.round(vendorCostPool * 0.27),
    days60: Math.round(vendorCostPool * 0.14),
    days90Plus: Math.round(vendorCostPool * 0.07),
  };
  const accountsPayable = apAging.current + apAging.days30 + apAging.days60 + apAging.days90Plus;

  const cashFlowForecast = REVENUE_HISTORY.slice(-6).map((m) => {
    const outflow = Math.round(m.mrr * (1 - GROSS_PROFIT_PERCENT / 100));
    return { month: m.month, inflow: m.mrr, outflow, net: m.mrr - outflow };
  });

  const costTrends = REVENUE_HISTORY.slice(-12).map((m) => ({
    month: m.month,
    cost: Math.round(m.mrr * (1 - GROSS_PROFIT_PERCENT / 100) * rng.float(0.96, 1.04)),
  }));

  const clientProfitability = CLIENTS.filter((c) => c.status === "active")
    .map((c) => {
      const costRatio = rng.float(0.28, 0.55);
      const margin = Math.round(c.mrr * (1 - costRatio));
      return { client: c, margin, marginPercent: Math.round((1 - costRatio) * 100) };
    })
    .sort((a, b) => b.margin - a.margin);

  const departmentCostView = REVENUE_BY_DEPARTMENT.map((d) => ({
    label: d.label,
    revenue: d.value,
    cost: Math.round(d.value * rng.float(0.35, 0.55)),
  }));

  return {
    mrr,
    grossProfit,
    grossProfitPercent: GROSS_PROFIT_PERCENT,
    payroll,
    collections,
    outstandingInvoices,
    accountsPayable,
    apAging,
    cashFlowForecast,
    revenueForecast: REVENUE_HISTORY.slice(-6),
    costTrends,
    clientProfitability: {
      top: clientProfitability.slice(0, 10),
      bottom: clientProfitability.slice(-10).reverse(),
    },
    departmentCostView,
  };
}

export async function fetchFinanceOverview() {
  return withLatency(computeFinanceOverview(), 300);
}

export function useFinanceOverview() {
  return useQuery({ queryKey: ["finance-overview"], queryFn: fetchFinanceOverview, staleTime: 45_000 });
}
